import React, { FC } from 'react';
import ReactModal from 'react-modal';
import classnames from 'classnames';

import { useReactModalAnimationWorkaround } from '../../hooks/helpers/useReactModalAnimationWorkaround';

import { RSDialogButton } from './components/RSDialogButton';

import styles from './index.module.css';

export type RSDialogProps =
  | RSDialogDefaultProps
  | RSDialogOnlyActionProps
  | RSDialogDestructiveProps;

export interface RSDialogDefaultProps {
  type: 'default';
  isOpen: boolean;
  onRequireClosing: () => void;
  title: string;
  description: string;
  actionText: string;
  onRequireAction: () => void;
  cancelText: string;
  onRequireCancellation: () => void;
}

export interface RSDialogOnlyActionProps {
  type: 'onlyAction';
  isOpen: boolean;
  onRequireClosing: () => void;
  title: string;
  description: string;
  actionText: string;
  onRequireAction: () => void;
}

export interface RSDialogDestructiveProps {
  type: 'destructive';
  isOpen: boolean;
  onRequireClosing: () => void;
  title: string;
  description: string;
  destructiveText: string;
  onRequireDestruction: () => void;
  cancelText: string;
  onRequireCancellation: () => void;
}

export const RSDialog: FC<RSDialogProps> = ({
  isOpen,
  onRequireClosing,
  title,
  description,
  ...props
}) => {
  const { afterOpenModal, afterClose, wasOpen } =
    useReactModalAnimationWorkaround(false);

  return (
    <ReactModal
      isOpen={isOpen}
      className={{
        base: classnames(
          'rounded-15 pc:w-[400px] mx-auto w-[320px] overflow-hidden bg-white',
          styles.contentBase,
        ),
        afterOpen: '',
        beforeClose: classnames(styles.contentBaseBeforeClose),
      }}
      overlayClassName={{
        base: classnames('fixed z-8 px-1.5', styles.overlayBase),
        afterOpen: classnames(wasOpen && styles.overlayBaseAfterOpen),
        beforeClose: styles.overlayBaseBeforeClose,
      }}
      closeTimeoutMS={200}
      onRequestClose={onRequireClosing}
      onAfterOpen={afterOpenModal}
      onAfterClose={afterClose}
      shouldFocusAfterRender={false}
    >
      <div className="box-border flex w-full flex-col items-center px-2.5 pb-3 pt-4">
        <div className="rs-font text-16 text-gray-1000 pc:text-15 mb-1 font-bold">
          {title}
        </div>
        <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
          {description}
        </div>
      </div>

      <div className="box-border border-b-0 border-l-0 border-r-0 border-t border-solid border-gray-50">
        {(() => {
          if (!('cancelText' in props)) {
            return (
              <div>
                <RSDialogButton
                  label={props.actionText}
                  variant="action"
                  isRoundedBR
                  isRoundedBL
                  onClick={props.onRequireAction}
                />
              </div>
            );
          }

          if ('destructiveText' in props) {
            return (
              <div className="flex">
                <RSDialogButton
                  label={props.cancelText}
                  variant="cancel"
                  isRoundedBL
                  onClick={props.onRequireCancellation}
                />
                <div className="flex-none border-b-0 border-l-0 border-r border-t-0 border-solid border-gray-50" />
                <RSDialogButton
                  label={props.destructiveText}
                  variant="destructive"
                  isRoundedBR
                  onClick={props.onRequireDestruction}
                />
              </div>
            );
          }

          return (
            <div className="flex">
              <RSDialogButton
                label={props.cancelText}
                variant="cancel"
                isRoundedBL
                onClick={props.onRequireCancellation}
              />
              <div className="flex-none border-b-0 border-l-0 border-r border-t-0 border-solid border-gray-50" />
              <RSDialogButton
                label={props.actionText}
                variant="action"
                isRoundedBR
                onClick={props.onRequireAction}
              />
            </div>
          );
        })()}
      </div>
    </ReactModal>
  );
};
